import React, { useState, useEffect, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import ViewForm from './Form';
import { hasValue, getLastQuaters, hasEnabledFeature } from 'globalutils/fmUtils';
import { UI_LOGBOOK_DATE, DATE_FORMAT_REGEX, DATE_FORMAT_REPLACEMENT } from 'globalconfig/constants/static';
import { mediaUrls } from 'globalconfig/constants/mediaUrls';

const layout = window?.$environment?.CURRENT_LAYOUT;
const Message = lazy(() => import(`globalcomponents/UI/Message/${layout}`));
const CheckBox = lazy(() => import(`globalcomponents/UI/Toggle/${layout}`));
const Table = lazy(() => import(`globalcomponents/UI/Table/${layout}`));
const Export = lazy(() => import(`globalcomponents/UI/Export/${layout}`));
const FormStyle = require(`globalcomponents/UI/Form/${layout}/serverStyle`).default;
const Label = lazy(() => import(`globalcomponents/UI/FormLabel/${layout}`));
const Input = lazy(() => import(`globalcomponents/UI/Input/${layout}`));
const SvgIcon = lazy(() => import(`globalcomponents/SvgIcon/${layout}`));

const Index = ({ theme, ...contentProps }) => (
  <Box style={{ ...theme.palette.contentStyle }}>
    <IFTAComponent {...contentProps} />
  </Box>
);

export default Index;

const IFTAComponent = ({
  apiStatus,
  callback,
  history,
  Styled,
  fetchIftaMileage,
  getIftaAuditCSV,
  handleExportIfta,
  generateReport,
  ifta,
  filterList,
  vehicles,
  iftaApiId,
  logBook,
  commonProps,
  getDocumentToken,
  getDocumentTokenApiId,
  isOldView,
}) => {
  const { i18n } = useTranslation();
  const [hideStates, setHideStates] = useState(true);
  const [pageination, setPageination] = useState(false);
  const hasApiStatus = hasValue(apiStatus.status, 'api', fetchIftaMileage);
  const beBaseUrl = window?.$environment.BE_BASE_URL;

  const information = hasApiStatus?.errorMessage ? (
    <Message type="error" s message={i18n.t(hasApiStatus.errorMessage)} />
  ) : (
    i18n.t('common.tableEmptyMessage')
  );

  useEffect(() => {
    filterList(hideStates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideStates]);

  const lastQuater = getLastQuaters(true);
  const [values, setValues] = useState({
    id: '',
    fromOdometer: 'true',
    dateTimeFrom: lastQuater[0].format(UI_LOGBOOK_DATE),
    dateTimeTo: lastQuater[1].format(UI_LOGBOOK_DATE),
  });

  useEffect(() => {
    if (ifta?.downloadUrl) {
      const url = `${beBaseUrl.replace(/\/api/g, '')}${ifta.downloadUrl}?token=${logBook?.documentToken}`;
      document.getElementById('downloadTruckGpsHistory').src = url;
    }
  }, [ifta?.downloadUrl]);

  const exportIfta = (data) => {
    getDocumentToken({ apiId: getDocumentTokenApiId });
    handleExportIfta({ id: 'exportIfta', data });
  };

  const formatDate = (dateStr) => dateStr?.replace(DATE_FORMAT_REGEX, DATE_FORMAT_REPLACEMENT);

  const fetchReport = (fields) => {
    getDocumentToken({ apiId: getDocumentTokenApiId });
    generateReport({
      apiId: 'fetchIFTAReport',
      data: {
        id: fields.vehicleType === 'inactive' && fields.id.label === 'All' ? '0' : fields.id.value,
        fromOdometer: fields.fromOdometer,
        dateTimeFrom: formatDate(fields.dateTimeFrom),
        dateTimeTo: formatDate(fields.dateTimeTo),
      },
      callback,
    });
  };

  const columns = [
    { id: 'state', numeric: false, disablePadding: true, label: i18n.t('iftaMileage.Jurisdiction'), needSorting: true },
    { id: 'totalMiles', numeric: true, disablePadding: false, label: i18n.t('iftaMileage.totalMiles'), needSorting: true },
    ...(hasEnabledFeature('taxableMilesViewEnabled')
      ? [
          {
            id: 'totalTaxableMiles',
            numeric: true,
            disablePadding: false,
            label: i18n.t('iftaMileage.totalTaxableMiles'),
            needSorting: true,
          },
        ]
      : []),
    { id: 'fuelUsage', numeric: true, disablePadding: false, label: i18n.t('iftaMileage.fuelUsage'), needSorting: true },
  ];

  const tableData = (fields) => setValues({ ...fields });
  const generateRows = () => ifta?.filteredList || [];

  return (
    <Styled.Wrapper>
      <p className="searchTitle">{i18n.t(isOldView ? 'iftaMileage.truck' : 'iftaMileage.search')}</p>
      <Styled.TopWrapper>
        <ViewForm
          {...{
            apiStatus,
            history,
            callback,
            Styled,
            hasApiStatus,
            vehicles,
            iftaApiId,
            fetchIftaMileage: (data) => {
              setHideStates(true);
              setPageination(true);
              fetchIftaMileage(data);
            },
            handleExportIfta,
            fetchReport,
            tableData,
            commonProps,
            isOldView,
            i18n,
          }}
        />
      </Styled.TopWrapper>

      <Styled.TableWrapper>
        {ifta.iftaErrors && (
          <div className="iftaErrorsSection showIftaError">
            <SvgIcon value={mediaUrls.confirmIcon} alt="warn" />
            We found error(s) for this date range. View the report{' '}
            <a onClick={() => getIftaAuditCSV({ id: 'iftaErrorsCsv' })}>here</a>
          </div>
        )}

        <div className="exportSection">
          <CheckBox
            type="NormalGrey"
            label={i18n.t('iftaMileage.hideLabel')}
            checked={hideStates}
            onClick={() => setHideStates(!hideStates)}
            className="enable"
          />
          {!isOldView && (
            <Export
              src={mediaUrls.new_export_circle}
              filename={'users'}
              toolTip={i18n.t('iftaMileage.tooltip')}
              className="exportContainer"
              buttonName={i18n.t('iftaMileage.tooltip')}
              onClick={() =>
                exportIfta({
                  id: values?.id?.value || '',
                  fromOdometer: values.fromOdometer,
                  dateTimeFrom: formatDate(values.dateTimeFrom),
                  dateTimeTo: formatDate(values.dateTimeTo),
                })
              }
            />
          )}
        </div>

        <Table
          type="client"
          defaultSortBy={{ sortBy: 'state', order: 'asc' }}
          columns={columns}
          data={generateRows()}
          emptyMessage={information}
          itemsPerPage={15}
          actions={[]}
          i18n={i18n}
          pageinationReset={pageination}
          pagenationClicked={() => setPageination(!pageination)}
        />
      </Styled.TableWrapper>

      {isOldView ? (
        <>
          <FormStyle.FieldsWrapper className="fieldSection">
            <Label variant="body1">{i18n.t('common.total')}</Label>
            <FormStyle.TextWrapper>
              <Input value={ifta?.total} disabled />
            </FormStyle.TextWrapper>
          </FormStyle.FieldsWrapper>
          <Export
            src={mediaUrls.new_export_circle}
            filename={'users'}
            toolTip={i18n.t('iftaMileage.tooltip')}
            className="exportContainer"
            buttonName={i18n.t('iftaMileage.tooltip')}
            onClick={() =>
              exportIfta({
                id: values?.id?.value || '',
                fromOdometer: values.fromOdometer,
                dateTimeFrom: formatDate(values.dateTimeFrom),
                dateTimeTo: formatDate(values.dateTimeTo),
              })
            }
          />
        </>
      ) : (
        <p className="total">
          {i18n.t('common.total')}
          <span className="amount">{Math.round((ifta.total + Number.EPSILON) * 100) / 100}</span>
        </p>
      )}

      <iframe id="downloadTruckGpsHistory" style={{ display: 'none' }}></iframe>
    </Styled.Wrapper>
  );
};

IFTAComponent.propTypes = {
  history: PropTypes.object.isRequired,
  apiStatus: PropTypes.object.isRequired,
  callback: PropTypes.func.isRequired,
  fetchIftaMileage: PropTypes.func.isRequired,
  getIftaAuditCSV: PropTypes.func,
  handleExportIfta: PropTypes.func,
  generateReport: PropTypes.func,
  ifta: PropTypes.object,
  filterList: PropTypes.func,
  vehicles: PropTypes.array,
  iftaApiId: PropTypes.string,
  logBook: PropTypes.object,
  commonProps: PropTypes.object,
  getDocumentToken: PropTypes.func,
  getDocumentTokenApiId: PropTypes.string,
  isOldView: PropTypes.bool,
};
